import { get } from 'lodash';
import { Dropbox, DropboxAuth } from 'dropbox';
import { UPLOAD_SOURCES, getLogger } from '@cld/upload-widget-common';
import appConfig from 'appConfig';
import { isSafari, openWindow } from 'utils/domUtils';
import { common as servicesCommon } from 'services';

const logger = getLogger();
const { clientId, apiDownloadUrl, apiDownloadHeader } = appConfig.dropbox;
const IMG_EXTS = ['jpg', 'jpeg', 'png', 'tiff', 'tif', 'gif', 'bmp'];

let authWin = null;

export default () => {
  logger.log(`[dropboxService]: creating dropbox api for client id: ${clientId}`);

  const getAuthInfo = (cloudName) => ({ token: servicesCommon.getStoredAuthToken(cloudName, UPLOAD_SOURCES.DROPBOX) });

  const setAuthListener = (cloudName) =>
    servicesCommon
      .setMessageAuthListener(() => authWin, cloudName, UPLOAD_SOURCES.DROPBOX)
      .then((result) => {
        authWin = null;
        return result;
      });

  const openAuthWindow = (options) => {
    const dbx = new DropboxAuth({ clientId: options.externalKey || clientId });

    const redirectUrl = servicesCommon.getAuthRedirectUrl(options.newTlsDomain);
    dbx
      .getAuthenticationUrl(redirectUrl)
      .then((loginUrl) => {
        logger.log(`[dropboxService]: created dropbox login link: ${loginUrl}`);
        authWin = openWindow(loginUrl, 'DropboxAuthwin');
      })
      .catch((error) => {
        logger.error('[dropboxService]: unable to get authentication url', error);
      });
  };

  const logout = async (params) => {
    const dbx = new Dropbox({ accessToken: params.token });

    try {
      await dbx.authTokenRevoke();
    } finally {
      servicesCommon.removeAuthToken(params.cloudName, UPLOAD_SOURCES.DROPBOX);
    }
  };

  const getAccountInfo = (params) => {
    const dbx = new Dropbox({ accessToken: params.token });
    logger.log('[dropboxService]: about to retrieve account info');
    return dbx.usersGetCurrentAccount();
  };

  const getFileInfo = async (dbx, path, size, format = 'jpeg') =>
    await dbx.filesGetThumbnail({
      path,
      format,
      size,
    });

  const getThumbnail = (fileObject) => (fileObject?.fileBlob ? window.URL.createObjectURL(fileObject.fileBlob) : '');

  const prepareFilesForDisplay = (files, dbx, size = 'w250') =>
    files.map((file) => {
      const item = file.result || file;

      item.link = item.url = item.path_lower;
      item.id = item.path_lower;

      const fileExt = IMG_EXTS.find((ext) => ~item.name.indexOf(ext));

      item.retrieveThumb = async () => {
        // provide a way to get the thumb as a separate action
        let result = item.thumbnail;

        if (fileExt) {
          try {
            const info = await getFileInfo(dbx, item.id, size);
            result = getThumbnail(info.result);
          } catch (ex) {
            logger.log(`[dropboxService]: failed to retrieve thumbnail for dropbox item: ${item.id}`);
          }
        }

        item.retrieveThumb = null; // remove so cannot be used again
        return result;
      };

      return item;
    });

  const getFoldersList = async (params) => {
    const dbx = new Dropbox({ accessToken: params.token });

    return Promise.all([dbx.filesListFolder({ path: params.path || '' }), params.path ? dbx.filesGetMetadata({ path: params.path }) : {}]).then(
      (results) => {
        logger.log('[dropboxService]: retrieved folder list data ', results);

        const entries = get(results, '[0].result.entries');

        return {
          folders: {},
          metadata: results[1],
          items: entries ? prepareFilesForDisplay(entries, dbx, 'w32h32') : {},
        };
      }
    );
  };

  const getFilesWithMetadata = (files, dbx) =>
    files
      ? Promise.all(
          files.map((item) =>
            dbx.filesGetMetadata({
              path: item.metadata.metadata.id,
              include_media_info: true,
            })
          )
        )
      : [];

  const getItemTitle = (item) =>
    item?.media_info?.metadata?.dimensions ? `${item.media_info.metadata.dimensions.width} × ${item.media_info.metadata.dimensions.height}` : '';

  const getFilesForGrid = (files, dbx) => {
    const forDisplay = prepareFilesForDisplay(files, dbx, 'w640h480'); // Add thumbnail to each file.

    return forDisplay.map((item) => ({
      ...item,
      title: getItemTitle(item),
      subtitle: item.name,
    }));
  };

  const shouldExtendResults = (params) => !params.cursor || params.hasMore === true;

  const getSearchResults = async (params) => {
    const dbx = new Dropbox({ accessToken: params.token });

    logger.log(`[dropboxService]: search called with query term: ${params.term}`);

    const dropboxResult =
      params.term && shouldExtendResults(params)
        ? await dbx.filesSearchV2({
            path: '',
            query: params.term,
            start: params.cursor,
            max_results: appConfig.dropbox.maxResults,
          })
        : null;

    const results = dropboxResult?.result;

    const filesWithMetaData = results ? await getFilesWithMetadata(results.matches, dbx) : [];

    const gridFiles = getFilesForGrid(filesWithMetaData, dbx);

    return {
      items: params.cursor ? params.items.concat(gridFiles) : gridFiles,
      metadata: null,
      cursor: results?.start || 0,
      hasMore: !!(results && results.more),
    };
  };

  // todo: separate the retrieval of thumbnails from the results - the thumbnails should be added when done but results
  // todo: should be returned immediately for display

  const getRecents = async (params) => {
    const dbx = new Dropbox({ accessToken: params.token });

    logger.log('[dropboxService]: retrieving recent files');

    let dropboxResults = {};

    if (!params.cursor) {
      dropboxResults = await dbx.filesListFolder({
        path: '',
        recursive: true,
        include_media_info: true,
      });
    } else if (params.hasMore) {
      dropboxResults = await dbx.filesListFolderContinue({ cursor: params.cursor });
    }

    const results = dropboxResults?.result;

    const fileResults = results?.entries
      ? results.entries.filter((item) => item['.tag'] === 'file') // filter to only files
      : [];

    const gridFiles = fileResults ? getFilesForGrid(fileResults, dbx) : [];

    const items = params.items && params.cursor ? params.items.concat(gridFiles) : gridFiles;

    return {
      items,
      metadata: null,
      cursor: results?.cursor || 0,
      hasMore: !!results?.has_more,
    };
  };

  const getExtension = (path) => path.split('.').slice(-1)[0] || '';

  const getFileName = (path) => path.split('/').slice(-1)[0] || `${new Date()}.${getExtension(path)}`;

  const isImage = (extension) => ~IMG_EXTS.indexOf(extension);

  const getFileInfoForQueue = async (dbx, path, size) => {
    let info = {};

    try {
      info = await getFileInfo(dbx, path, size);
    } catch (e) {
      logger.devError(`[dropboxService]: failed to retrieve info & thumbnail for uploaded dropbox item: ${path}`, e);
    }

    return info.result;
  };

  const fetchFile = async (dbx, path) => {
    const getItemObject = (item) => {
      let obj = item.fileBlob;

      try {
        obj = isSafari() ? obj : new File([item.fileBlob], item.name);

        if (!obj.name) {
          // probably in safari which doesnt support creating a new File - set the name for the upload so its not uploaded as 'blob'
          obj.name = item.name;
        }
      } catch (ex) {
        logger.warn('[dropboxService]: failed to get File from dropbox blob: ', ex);
      }

      return obj;
    };

    const item = await dbx.filesDownload({ path });

    return item?.result ? getItemObject(item.result) : null;
  };

  const fetchImageDimensions = async (dbx, file) => {
    const metadata = await dbx.filesGetMetadata({
      path: file.id,
      include_media_info: true,
    });

    return metadata?.result?.media_info?.metadata?.dimensions;
  };

  const getAsyncImageData = ({ dbx, isImageExt, shouldCreateBinary, path, file }) => {
    return Promise.all([isImageExt && shouldCreateBinary && fetchFile(dbx, path), isImageExt && fetchImageDimensions(dbx, file)]);
  };

  const generateFileObject = async ({ file, token, shouldCreateBinary }) => {
    const dbx = new Dropbox({ accessToken: token });
    const path = file.path_lower;
    const extension = getExtension(path);
    const info = await getFileInfoForQueue(dbx, path, 'w960h640');
    const isImageExt = isImage(extension);

    const [binary, imageDimensions] = await getAsyncImageData({ dbx, isImageExt, shouldCreateBinary, path, file });

    return {
      file: apiDownloadUrl,
      headers: `${apiDownloadHeader}${path}"} \r\n Authorization: Bearer ${token}`,
      // type using "application/" as a generic type to bypass crop for non image files
      type: isImageExt ? `image/${extension}` : `application/${extension}`,
      name: file.name,
      size: file.size,
      thumbnail: getThumbnail(info),
      binary,
      fetchImageDimensions: imageDimensions,
      filenameOverride: file.name || getFileName(path),
    };
  };

  const getUrlsForUpload = async ({ files, token, shouldCreateBinary }) =>
    await Promise.all([...files].map((file) => generateFileObject({ file, token, shouldCreateBinary })));

  // todo: consider building a cancellation mechanism to stop running action when another begins...
  const retrieveItemsThumbs = async (items) => await Promise.all(items.map((item) => (item.retrieveThumb ? item.retrieveThumb() : item.thumbnail)));

  return {
    getAuthInfo,
    openAuthWindow,
    setAuthListener,
    logout,
    getAccountInfo,
    getFoldersList,
    getSearchResults,
    getRecents,
    getUrlsForUpload,
    retrieveItemsThumbs,
  };
};
